/**
 * Vision-QA report per asset: expected vs transcribed text with word-level mismatch highlighting,
 * the issues list (element · severity · kind) and the AI-look score. "Fix" on an issue hands the flawed
 * element to the caller (→ creatives.regenerate_asset, which regenerates ONLY that element).
 */
import { CheckCircle2, CircleAlert, ScanText, Wand2 } from "lucide-react";
import { useMemo, useState } from "react";
import type { CreativeAsset, QaIssue } from "@/api/types";
import { cn } from "@/lib/cn";
import { dateTime, humanize } from "@/lib/format";
import { Pill } from "../ui/Badge";
import { Button } from "../ui/Button";
import { Segmented } from "../ui/Form";
import { CreativeMedia, QaBadge } from "./CreativeBits";

// ─── text matching ────────────────────────────────────────────────────────────

const norm = (s: string) => s.replace(/[‘’ʼ]/g, "'").replace(/[“”]/g, '"').replace(/\s+/g, " ").trim();
const tokens = (s: string) => norm(s).split(" ").filter(Boolean);

type DiffTok = { t: string; op: "same" | "add" | "del" };

/** Word-level LCS diff: `del` = expected word missing from render, `add` = rendered word not expected. */
export function wordDiff(expected: string, actual: string): DiffTok[] {
  const a = tokens(expected);
  const b = tokens(actual);
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array<number>(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      const row = dp[i] as number[];
      row[j] = a[i] === b[j] ? ((dp[i + 1] as number[])[j + 1] ?? 0) + 1 : Math.max((dp[i + 1] as number[])[j] ?? 0, row[j + 1] ?? 0);
    }
  }
  const out: DiffTok[] = [];
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) {
      out.push({ t: b[j] as string, op: "same" });
      i++;
      j++;
    } else if (((dp[i + 1] as number[])[j] ?? 0) >= ((dp[i] as number[])[j + 1] ?? 0)) {
      out.push({ t: a[i] as string, op: "del" });
      i++;
    } else {
      out.push({ t: b[j] as string, op: "add" });
      j++;
    }
  }
  while (i < m) out.push({ t: a[i++] as string, op: "del" });
  while (j < n) out.push({ t: b[j++] as string, op: "add" });
  return out;
}

function similarity(a: string, b: string): number {
  const d = wordDiff(a, b);
  const same = d.filter((x) => x.op === "same").length;
  const total = Math.max(tokens(a).length, tokens(b).length, 1);
  return same / total;
}

interface LineMatch {
  expected: string | null;
  actual: string | null;
  exact: boolean;
}

export function matchLines(expected: string[], transcribed: string[]): LineMatch[] {
  const used = new Set<number>();
  const rows: LineMatch[] = [];
  for (const e of expected) {
    let best = -1;
    let bestScore = 0;
    transcribed.forEach((t, idx) => {
      if (used.has(idx)) return;
      const s = norm(e) === norm(t) ? 2 : similarity(e, t);
      if (s > bestScore) {
        bestScore = s;
        best = idx;
      }
    });
    if (best >= 0 && bestScore >= 0.25) {
      used.add(best);
      const actual = transcribed[best] as string;
      rows.push({ expected: e, actual, exact: norm(e) === norm(actual) });
    } else rows.push({ expected: e, actual: null, exact: false });
  }
  transcribed.forEach((t, idx) => {
    if (!used.has(idx)) rows.push({ expected: null, actual: t, exact: false });
  });
  return rows;
}

// ─── view ─────────────────────────────────────────────────────────────────────

const qaDot = (s: string | undefined) => (s === "fail" ? "bg-danger" : s === "pass" ? "bg-success" : "bg-subtle");

export function AiLookMeter({ score }: { score: number }) {
  const v = Math.max(0, Math.min(1, score));
  const label = v < 0.3 ? "Looks real" : v < 0.6 ? "Slightly synthetic" : "Looks AI";
  const tone = v < 0.3 ? "bg-success" : v < 0.6 ? "bg-amber" : "bg-danger";
  const text = v < 0.3 ? "text-success" : v < 0.6 ? "text-amber" : "text-danger";
  return (
    <div className="flex items-center gap-3">
      <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-elevated" role="meter" aria-valuemin={0} aria-valuemax={1} aria-valuenow={v} aria-label="AI-look score">
        <div className="absolute inset-y-0 left-0 w-full bg-gradient-to-r from-success/25 via-amber/25 to-danger/25" />
        <div className={cn("absolute inset-y-0 left-0 rounded-full", tone)} style={{ width: `${Math.max(4, v * 100)}%` }} />
      </div>
      <span className={cn("w-[112px] shrink-0 text-right text-[12px] font-medium tnum", text)}>
        {label} · {v.toFixed(2)}
      </span>
    </div>
  );
}

function DiffLine({ row }: { row: LineMatch }) {
  if (row.expected && row.actual) {
    if (row.exact) return <span className="text-fg">{row.actual}</span>;
    return (
      <span className="leading-relaxed">
        {wordDiff(row.expected, row.actual).map((d, i) => (
          <span key={i}>
            {d.op === "same" && <span className="text-fg">{d.t}</span>}
            {d.op === "add" && <mark className="rounded bg-danger-soft px-0.5 text-danger">{d.t}</mark>}
            {d.op === "del" && <del className="rounded bg-amber-soft px-0.5 text-amber decoration-amber/70">{d.t}</del>}{" "}
          </span>
        ))}
      </span>
    );
  }
  if (row.expected) return <span className="italic text-danger">Not found in render</span>;
  return <mark className="rounded bg-danger-soft px-1 text-danger">{row.actual}</mark>;
}

export function QaReportView({ assets, onFix, className }: { assets: CreativeAsset[]; onFix?: (asset: CreativeAsset, issue: QaIssue | null) => void; className?: string }) {
  const live = useMemo(() => assets.filter((a) => a.status !== "superseded"), [assets]);
  const [sel, setSel] = useState<string | null>(null);
  const cur = live.find((a) => a.id === sel) ?? live[0];
  if (!cur) return null;
  const r = cur.qaReport;
  const rows = r ? matchLines(r.expectedText ?? [], r.transcribedText ?? []) : [];
  const history = assets.filter((a) => a.status === "superseded" && a.aspect === cur.aspect).reverse();
  const mismatches = rows.filter((x) => !x.exact).length;
  return (
    <section className={cn("rounded-card border border-line bg-surface", className)} aria-label="Vision QA">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-3">
        <div className="flex items-center gap-2 text-[14px] font-semibold">
          <ScanText className="size-4 text-lavender" strokeWidth={1.75} />
          Vision QA
        </div>
        {live.length > 1 && (
          <Segmented
            size="sm"
            value={cur.id}
            onChange={setSel}
            options={live.map((a) => ({
              value: a.id,
              label: (
                <span className="flex items-center gap-1.5">
                  {a.aspect}
                  <span className={cn("size-1.5 rounded-full", qaDot(a.qaStatus ?? a.qaReport?.verdict))} />
                </span>
              ),
            }))}
          />
        )}
      </div>
      <div className="flex flex-col gap-4 p-4">
        <div className="flex flex-wrap items-center gap-2 text-[12px] text-muted">
          <QaBadge asset={cur} />
          {r?.model && <span>by {r.model}</span>}
          {r?.checkedAt && <span>· {dateTime(r.checkedAt)}</span>}
          {typeof cur.attempts === "number" && cur.attempts > 1 && <span>· {cur.attempts} attempts</span>}
          {cur.model && <span>· rendered with {cur.model}</span>}
        </div>
        {!r ? (
          <p className="text-[12.5px] text-muted">
            {cur.qaStatus === "skipped" ? "QA was skipped for this asset (uploaded or imported media)." : "QA hasn't run on this asset yet. It runs automatically once the render is ready."}
          </p>
        ) : (
          <>
            {typeof r.aiLookScore === "number" && (
              <div>
                <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-subtle">AI-look score</div>
                <AiLookMeter score={r.aiLookScore} />
              </div>
            )}
            <div>
              <div className="mb-1.5 flex items-center justify-between gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-subtle">Text check · expected vs rendered</span>
                {rows.length > 0 &&
                  (mismatches === 0 ? (
                    <Pill tone="success" size="xs" icon={<CheckCircle2 className="size-3" />}>
                      Exact match
                    </Pill>
                  ) : (
                    <Pill tone="danger" size="xs">
                      {mismatches} mismatch{mismatches > 1 ? "es" : ""}
                    </Pill>
                  ))}
              </div>
              {rows.length === 0 ? (
                <p className="text-[12.5px] text-muted">No text expected or found.</p>
              ) : (
                <div className="overflow-hidden rounded-lg border border-line">
                  <div className="grid grid-cols-2 bg-surface-2 px-3 py-1.5 text-[11px] font-medium text-subtle">
                    <span>Expected</span>
                    <span>Transcribed (exactly as rendered)</span>
                  </div>
                  {rows.map((row, i) => (
                    <div key={i} className={cn("grid grid-cols-2 gap-3 border-t border-line px-3 py-2 text-[12.5px]", !row.exact && "bg-danger-soft/25")}>
                      <span className={cn(row.expected ? "text-fg/85" : "italic text-subtle")}>{row.expected ?? "— not expected —"}</span>
                      <span className="flex items-start gap-1.5">
                        {row.exact ? <CheckCircle2 className="mt-0.5 size-3.5 shrink-0 text-success" /> : <CircleAlert className="mt-0.5 size-3.5 shrink-0 text-danger" />}
                        <DiffLine row={row} />
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-subtle">Issues</div>
              {(r.issues ?? []).length === 0 ? (
                <p className="flex items-center gap-1.5 text-[12.5px] text-success">
                  <CheckCircle2 className="size-3.5" /> No issues found.
                </p>
              ) : (
                <ul className="flex flex-col gap-1.5">
                  {(r.issues ?? []).map((iss, i) => (
                    <li key={i} className="flex items-start gap-2.5 rounded-lg border border-line bg-surface-2 px-3 py-2">
                      <Pill tone={iss.severity === "major" ? "danger" : "amber"} size="xs" className="mt-px">
                        {iss.severity}
                      </Pill>
                      <div className="min-w-0 flex-1 text-[12.5px]">
                        <div className="flex flex-wrap items-center gap-1.5">
                          {iss.element && <span className="font-semibold text-fg">{iss.element}</span>}
                          <span className="text-muted">{humanize(iss.kind)}</span>
                        </div>
                        {iss.detail && <p className="mt-0.5 leading-snug text-fg/80">{iss.detail}</p>}
                      </div>
                      {onFix && (
                        <Button size="xs" variant="ghost" icon={<Wand2 className="size-3.5" strokeWidth={1.75} />} onClick={() => onFix(cur, iss)}>
                          Fix
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
        {history.length > 0 && (
          <div>
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-subtle">Earlier attempts · {cur.aspect}</div>
            <ul className="flex flex-col gap-1.5">
              {history.map((h) => {
                const raw = h as unknown as { editInstruction?: string | null; error?: string | null };
                const v = h.qaStatus ?? h.qaReport?.verdict;
                return (
                  <li key={h.id} className="flex items-center gap-2.5 rounded-lg border border-line bg-surface-2 p-2">
                    <span className="size-10 shrink-0 overflow-hidden rounded-md opacity-80">
                      <CreativeMedia url={h.url} poster={h.thumbnailUrl} mediaType={h.mediaType} className="size-full" />
                    </span>
                    <div className="min-w-0 flex-1 text-[12px]">
                      <div className="flex items-center gap-1.5">
                        <span className="text-muted">Attempt {h.attempts ?? "—"}</span>
                        {v === "fail" ? (
                          <Pill tone="danger" size="xs">
                            QA fail{h.qaReport?.issues?.length ? ` · ${h.qaReport.issues.length}` : ""}
                          </Pill>
                        ) : v === "pass" ? (
                          <Pill tone="success" size="xs">
                            QA pass
                          </Pill>
                        ) : null}
                      </div>
                      {(raw.editInstruction || h.qaReport?.issues?.[0]?.detail) && (
                        <p className="mt-0.5 truncate text-subtle">{raw.editInstruction ? `Fix: ${raw.editInstruction}` : h.qaReport?.issues?.[0]?.detail}</p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}
